import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardHeader from '../common/DashboardHeader';
import {
  UsersIcon,
  BuildingStorefrontIcon,
  CreditCardIcon,
  ChartBarIcon,
  BellIcon,
  CogIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface AdminPrivileges {
  manage_garages: boolean;
  manage_users: boolean;
  manage_subscriptions: boolean;
  view_reports: boolean;
  manage_payments: boolean;
  send_notifications: boolean;
  view_analytics: boolean;
  export_data: boolean;
}

interface DashboardData {
  garages: {
    total: number;
    active: number;
    pending_approval: number;
    suspended: number;
  };
  users: {
    total: number;
    garage_owners: number;
    employees: number;
    customers: number;
  };
  subscriptions: {
    active: number;
    expiring_soon: number;
    trial: number;
    cancelled: number;
  };
  revenue: {
    monthly: number;
    commission: number;
    processing_fees: number;
  };
  recent_activities: Array<{
    id: number;
    type: string;
    description: string;
    timestamp: string;
    status: 'info' | 'success' | 'warning' | 'error';
  }>;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [privileges, setPrivileges] = useState<AdminPrivileges | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchAdminPrivileges();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Mock data - replace with actual API call
      const mockData: DashboardData = {
        garages: {
          total: 127,
          active: 98,
          pending_approval: 5,
          suspended: 3,
        },
        users: {
          total: 2847,
          garage_owners: 127,
          employees: 456,
          customers: 2264,
        },
        subscriptions: {
          active: 98,
          expiring_soon: 12,
          trial: 8,
          cancelled: 5,
        },
        revenue: {
          monthly: 42500,
          commission: 6375,
          processing_fees: 1250,
        },
        recent_activities: [
          {
            id: 1,
            type: 'garage_approval',
            description: 'New garage "Al-Khaleej Auto" pending approval',
            timestamp: '2024-01-15T10:30:00Z',
            status: 'warning',
          },
          {
            id: 2,
            type: 'subscription_renewal',
            description: 'Premium Motors renewed subscription',
            timestamp: '2024-01-15T09:15:00Z',
            status: 'success',
          },
          {
            id: 3,
            type: 'payment_failed',
            description: 'Payment failed for City Garage',
            timestamp: '2024-01-15T08:45:00Z',
            status: 'error',
          },
          {
            id: 4,
            type: 'user_registered',
            description: '15 new customers registered today',
            timestamp: '2024-01-15T08:00:00Z',
            status: 'info',
          },
        ],
      };

      await new Promise(resolve => setTimeout(resolve, 500));
      setDashboardData(mockData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminPrivileges = async () => {
    try {
      // Mock privileges data - replace with actual API call
      const mockPrivileges: AdminPrivileges = {
        manage_garages: true,
        manage_users: true,
        manage_subscriptions: true,
        view_reports: true,
        manage_payments: false, // Restricted privilege
        send_notifications: true,
        view_analytics: true,
        export_data: false, // Restricted privilege
      };

      setPrivileges(mockPrivileges);
    } catch (error) {
      console.error('Error fetching admin privileges:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KW', {
      style: 'currency',
      currency: 'KWD',
    }).format(amount);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'garage_approval':
        return <BuildingStorefrontIcon className="w-5 h-5" />;
      case 'subscription_renewal':
        return <CreditCardIcon className="w-5 h-5" />;
      case 'payment_failed':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      case 'user_registered':
        return <UsersIcon className="w-5 h-5" />;
      default:
        return <DocumentTextIcon className="w-5 h-5" />;
    }
  };

  const getActivityColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <ShieldCheckIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Welcome back, {user?.name}</p>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              {privileges?.manage_garages && (
                <button className="bg-[#008B8B] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#006666] flex items-center justify-center text-sm">
                  <BuildingStorefrontIcon className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Manage Garages</span>
                  <span className="sm:hidden">Garages</span>
                </button>
              )}
              {privileges?.send_notifications && (
                <button className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm">
                  <BellIcon className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Send Notification</span>
                  <span className="sm:hidden">Notify</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Privilege Alert */}
        {privileges && (!privileges.manage_payments || !privileges.export_data) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
              <p className="text-sm font-medium text-yellow-800">Limited Access</p>
            </div>
            <p className="text-xs text-yellow-700 mt-1">
              Some features are restricted by your admin privileges. Contact Super Admin for access to payment management and data export.
            </p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {/* Garages Stats */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Total Garages</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData?.garages.total}</p>
                <p className="text-xs text-green-600">{dashboardData?.garages.active} active</p>
              </div>
              <BuildingStorefrontIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B]" />
            </div>
          </div>

          {/* Users Stats */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Total Users</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData?.users.total}</p>
                <p className="text-xs text-blue-600">{dashboardData?.users.customers} customers</p>
              </div>
              <UsersIcon className="w-6 sm:w-8 h-6 sm:h-8 text-blue-600" />
            </div>
          </div>

          {/* Subscriptions Stats */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Active Subs</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{dashboardData?.subscriptions.active}</p>
                <p className="text-xs text-yellow-600">{dashboardData?.subscriptions.expiring_soon} expiring</p>
              </div>
              <CreditCardIcon className="w-6 sm:w-8 h-6 sm:h-8 text-green-600" />
            </div>
          </div>

          {/* Revenue Stats */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Monthly Revenue</p>
                <p className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {dashboardData ? formatCurrency(dashboardData.revenue.monthly) : '-'}
                </p>
                {privileges?.view_analytics && (
                  <p className="text-xs text-green-600">+12% vs last month</p>
                )}
              </div>
              <ChartBarIcon className="w-6 sm:w-8 h-6 sm:h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Quick Actions Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                {privileges?.manage_garages && (
                  <a
                    href="/dashboard/garages"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <BuildingStorefrontIcon className="w-5 h-5 text-[#008B8B] mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Manage Garages</p>
                      <p className="text-xs text-gray-500">{dashboardData?.garages.pending_approval} pending approval</p>
                    </div>
                  </a>
                )}

                {privileges?.manage_users && (
                  <a
                    href="/dashboard/users"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <UsersIcon className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">User Management</p>
                      <p className="text-xs text-gray-500">Manage platform users</p>
                    </div>
                  </a>
                )}

                {privileges?.manage_subscriptions && (
                  <a
                    href="/dashboard/subscriptions"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <CreditCardIcon className="w-5 h-5 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Subscriptions</p>
                      <p className="text-xs text-gray-500">{dashboardData?.subscriptions.expiring_soon} expiring soon</p>
                    </div>
                  </a>
                )}

                {privileges?.view_reports && (
                  <a
                    href="/dashboard/reports"
                    className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <ChartBarIcon className="w-5 h-5 text-purple-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Reports</p>
                      <p className="text-xs text-gray-500">View analytics</p>
                    </div>
                  </a>
                )}
              </div>
            </div>

            {/* Privileges Overview */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Your Privileges</h3>
              <div className="space-y-2">
                {privileges && Object.entries(privileges).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
                    {value ? (
                      <CheckCircleIcon className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircleIcon className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activities</h3>
                <button className="text-sm text-[#008B8B] hover:text-[#006666] flex items-center">
                  <EyeIcon className="w-4 h-4 mr-1" />
                  View All
                </button>
              </div>
              
              <div className="space-y-3">
                {dashboardData?.recent_activities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-full ${getActivityColor(activity.status)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;